import { useRouter } from 'next/router'
import Head from 'next/head'
import Image from 'next/image'

import { getPageBySlug, getAllPages } from '../lib/api'
import blocksProcessings from 'wordpress/blocksProcessing'

import Navbar from 'components/navbar'
import Layout, { Textlayout } from 'components/layout'
import Content from 'components/article/content'
import Commentform from 'components/article/commentForm'
import Allcomments from 'components/article/allComments'
import Footer from 'components/footer'
import Pagetitle from 'components/pageTitle'

export default function customPage({ page }) {
  const router = useRouter()

  if (router.isFallback) return <div>loading</div>

  return (
    <>
      <Head>
        <meta charSet="utf-8" />
        <meta name="robots" content={`${page.seo.metaRobotsNoindex}, ${page.seo.metaRobotsNofollow}`} />

        <title>{page.seo.title}</title>
        <meta name="title" content={page.seo.title} />
        <meta name="description" content={page.seo.metaDesc} />
        <link rel="canonical" href={`${process.env.NEXT_PUBLIC_BASEPATH}/${page.slug}`} />

        {/* Open graph (FB) */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${process.env.NEXT_PUBLIC_BASEPATH}/${page.slug}`} />

        {page.seo.opengraphTitle ? (
          <meta property="og:title" content={page.seo.opengraphTitle} />
        ) : (
          <meta property="og:title" content={page.seo.title} />
        )}
        {page.seo.opengraphDescription ? (
          <meta property="og:description" content={page.seo.opengraphDescription} />
        ) : (
          <meta property="og:description" content={page.seo.metaDesc} />
        )}
        {page.seo.opengraphImage ? (
          <>
            <meta property="og:image" content={page.seo.opengraphImage.sourceUrl} />
            <meta property="og:image:alt" content={page.seo.opengraphImage.altText} />
            <meta property="og:image:width" content={page.seo.opengraphImage.mediaDetails.width} />
            <meta property="og:image:height" content={page.seo.opengraphImage.mediaDetails.height} />
          </>
        ) : (
          page.featuredImage && (
            <>
              <meta property="og:image" content={page.featuredImage.node.mediaItemUrl} />
              <meta property="og:image:alt" content={page.featuredImage.node.altText} />
              <meta property="og:image:width" content={page.featuredImage.node.mediaDetails.width} />
              <meta property="og:image:height" content={page.featuredImage.node.mediaDetails.height} />
            </>
          )
        )}

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={`${process.env.NEXT_PUBLIC_BASEPATH}/${page.slug}`} />
        {page.seo.twitterTitle ? (
          <meta property="twitter:title" content={page.seo.twitterTitle} />
        ) : (
          <meta property="twitter:title" content={page.seo.title} />
        )}
        {page.seo.twitterTitle ? (
          <meta property="twitter:description" content={page.seo.twitterDescription} />
        ) : (
          <meta property="twitter:description" content={page.seo.metaDesc} />
        )}
        {page.seo.twitterImage?.mediaDetails ? (
          <>
            <meta property="twitter:image" content={page.seo.twitterImage.sourceUrl} />
            <meta property="twitter:image:alt" content={page.seo.twitterImage.altText} />
            <meta property="twitter:image:width" content={page.seo.twitterImage.mediaDetails.width} />
            <meta property="twitter:image:height" content={page.seo.twitterImage.mediaDetails.height} />
          </>
        ) : (
          page.featuredImage && (
            <>
              <meta property="twitter:image" content={page.featuredImage.node.mediaItemUrl} />
              <meta property="twitter:image:alt" content={page.featuredImage.node.altText} />
              <meta property="twitter:image:width" content={page.featuredImage.node.mediaDetails.width} />
              <meta property="twitter:image:height" content={page.featuredImage.node.mediaDetails.height} />
            </>
          )
        )}

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'WebPage',
              publisher: {
                '@id': 'https://ateliermilky.com#identity'
              },
              copyrightHolder: {
                '@id': 'https://ateliermilky.com#identity'
              },
              name: page.seo.title,
              description: page.seo.title
            })
          }}
        />
      </Head>

      <Navbar />
      <main className="relative mt-8 md:mt-16">
        <Layout>
          <Pagetitle>{page.title}</Pagetitle>

          {page.featuredImage && (
            <div className="mt-4">
              <Image
                src={page.featuredImage.node.mediaItemUrl}
                layout="responsive"
                width={page.featuredImage.node.mediaDetails.width}
                height={page.featuredImage.node.mediaDetails.height}
                alt={page.featuredImage.node.altText}
              />
            </div>
          )}
          <Textlayout>
            <div className="articleContent mt-8 text-lg">
              <Content blocks={page.blocks} completeSlug={process.env.NEXT_PUBLIC_BASEPATH + '/' + page.slug} />
            </div>
          </Textlayout>
        </Layout>
      </main>
      <div className="mt-32">
        <Footer />
      </div>
    </>
  )
}

export async function getStaticProps({ params }) {
  try {
    const page = await getPageBySlug(params.pageSlug)

    if (!page) return { notFound: true }
    const pageBlocksProccessed = await blocksProcessings(page)
    return {
      props: {
        page: pageBlocksProccessed
      },
      revalidate: 300 // 5 min
    }
  } catch (e) {
    console.error('An error occured while getting page props', e)
    return {
      notFound: true
    }
  }
}

export async function getStaticPaths() {
  const pages = await getAllPages()

  return {
    paths: pages.nodes.map((thisPage) => ({ params: { pageSlug: thisPage.slug } })),
    fallback: true
  }
}
